/**
 * GRI 2 - General Disclosures
 * Auto-populated organizational and governance disclosures
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Building, Shield, Users, FileText, CheckCircle, AlertCircle,
  ChevronDown, ChevronUp, ExternalLink, Lock
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const GRIGeneralDisclosures = ({ study }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState('Organization & Reporting');

  useEffect(() => {
    if (study?.id) {
      fetchDisclosures();
    }
  }, [study?.id]);

  const fetchDisclosures = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/gri-v2/${study.id}/general-disclosures`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (error) {
      console.error('Error fetching general disclosures:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSectionIcon = (sectionName) => {
    if (sectionName.includes('Organization')) return <Building className="w-5 h-5 text-blue-600" />;
    if (sectionName.includes('Governance')) return <Shield className="w-5 h-5 text-purple-600" />;
    if (sectionName.includes('Strategy')) return <FileText className="w-5 h-5 text-green-600" />;
    if (sectionName.includes('Stakeholder')) return <Users className="w-5 h-5 text-orange-600" />;
    return <FileText className="w-5 h-5 text-gray-600" />;
  };

  const getStatusColor = (status) => {
    if (status === 'complete') return 'text-green-600';
    if (status === 'partial') return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
          <p className="text-gray-600">Loading general disclosures...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="text-center text-gray-600 py-12">No disclosure data available</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">GRI 2 - General Disclosures</h2>
        <p className="text-blue-100">Organizational context, governance, and foundational information</p>
      </div>

      {/* Statistics */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-gray-900">{data.statistics.total}</div>
            <div className="text-sm text-gray-600">Total Disclosures</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{data.statistics.complete}</div>
            <div className="text-sm text-gray-600">Complete</div>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">{data.statistics.partial}</div>
            <div className="text-sm text-gray-600">Partial</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{data.statistics.auto_populated}</div>
            <div className="text-sm text-gray-600">Auto-Populated</div>
          </div>
        </div>
      </div>

      {/* Disclosure Sections */}
      <div className="space-y-4">
        {Object.entries(data.sections).map(([sectionName, disclosures]) => (
          <div key={sectionName} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* Section Header */}
            <button
              onClick={() => setExpandedSection(expandedSection === sectionName ? null : sectionName)}
              className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 transition"
            >
              <div className="flex items-center gap-3">
                {getSectionIcon(sectionName)}
                <div className="text-left">
                  <div className="font-semibold text-gray-900">{sectionName}</div>
                  <div className="text-sm text-gray-600">{disclosures.length} disclosures</div>
                </div>
              </div>
              {expandedSection === sectionName ? 
                <ChevronUp className="w-5 h-5 text-gray-600" /> : 
                <ChevronDown className="w-5 h-5 text-gray-600" />
              }
            </button>

            {/* Disclosure Details */}
            {expandedSection === sectionName && (
              <div className="divide-y divide-gray-200">
                {disclosures.map((disclosure, idx) => (
                  <div key={idx} className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                          {disclosure.disclosure_number}
                        </span>
                        <div>
                          <div className="font-semibold text-gray-900">{disclosure.title}</div>
                          <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                            Source: {disclosure.source}
                            {disclosure.source !== 'Manual' && <ExternalLink className="w-3 h-3" />}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {disclosure.auto_populated && (
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                            Auto-Populated
                          </span>
                        )}
                        {disclosure.status === 'complete' ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : disclosure.status === 'partial' ? (
                          <AlertCircle className="w-5 h-5 text-yellow-600" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                    </div>

                    {/* Data */}
                    {disclosure.data && (
                      <div className="bg-gray-50 rounded-lg p-4 mb-3">
                        <div className="text-xs font-semibold text-gray-700 mb-2">Organizational Data:</div>
                        <div className="space-y-1 text-sm text-gray-700">
                          {Object.entries(disclosure.data).map(([key, value]) => (
                            <div key={key} className="flex items-start">
                              <span className="font-medium text-gray-600 w-48">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</span>
                              <span className="flex-1">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Narrative */}
                    {disclosure.narrative && (
                      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="w-4 h-4 text-blue-600" />
                          <span className="text-xs font-semibold text-blue-700">Management Approach:</span>
                        </div>
                        <p className="text-sm text-gray-700">{disclosure.narrative}</p>
                      </div>
                    )}

                    {/* Manual Entry Required */}
                    {disclosure.status === 'missing' && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                        This disclosure requires manual data entry. Please provide the necessary information.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="text-sm text-blue-900">
          <p className="font-semibold mb-1">About GRI 2</p>
          <p>
            GRI 2 General Disclosures provide foundational information about your organization, governance structure, 
            and reporting practices. SAARV automatically populates these disclosures from Company Master, BoardOS, ERM, 
            and other operational modules to maintain a single source of truth.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GRIGeneralDisclosures;